import { Fragment, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ModuleIntro } from './ModuleIntro';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

/** One step in the trail. The last one has no href — it is the current page. */
export interface Crumb {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  backLink?: { label: string; href: string };
  /**
   * Full trail from the section root to here.
   *
   * Supersedes `backLink` when both are given: they answer the same question,
   * and a trail plus an arrow above it is two controls for going up.
   */
  breadcrumbs?: Crumb[];
  /**
   * Chips shown on the title line — status, class, flags.
   *
   * State belongs next to the name of the thing, where it is read first, rather
   * than in a detail table further down the page.
   */
  titleAdornment?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  backLink,
  breadcrumbs,
  titleAdornment,
  actions,
  className,
}: PageHeaderProps) {
  const hasCrumbs = !!breadcrumbs?.length;

  return (
    <div className={cn('mb-4', className)}>
      {hasCrumbs && (
        <Breadcrumb className="mb-1">
          <BreadcrumbList>
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                // The separator renders its own <li>, so it is a sibling of the
                // item rather than a child — nesting one inside the other is
                // invalid list markup.
                <Fragment key={`${crumb.label}-${i}`}>
                  <BreadcrumbItem>
                    {isLast || !crumb.href ? (
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast && <BreadcrumbSeparator />}
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      )}

      {backLink && !hasCrumbs && (
        <Link
          to={backLink.href}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-1"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLink.label}
        </Link>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          {/* h1: 24px / 700 — the display tier of the type scale. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">{title}</h1>
            {/* Every page gets "what is this" for free: the intro resolves the
                current route against the module registry, so a screen is
                explained the moment it is registered rather than when somebody
                remembers to write a paragraph for it. */}
            <ModuleIntro />
            {titleAdornment}
          </div>
          {description && <p className="mt-1 text-base text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
